import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Merge, Pencil, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, dataHoraBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

const Stat = ({ label, value, hint }) => (
  <div className="rounded-xl border border-white/10 bg-secondary/40 p-3">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-num text-sm">{value}</p>
    {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
  </div>
);

/** Ficha do cliente: totais, histórico de pedidos, edição e unificação de cadastros. */
export function CustomerDetailModal({ customerId, customers, onClose, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("VIEW");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data: res } = await api.get(`/customers/${customerId}`);
      setData(res);
      setForm({ name: res.name || "", phone: res.phone || "", notes: res.notes || "" });
    } catch (e) {
      toast.error(apiError(e));
      onClose();
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/customers/${customerId}`, form);
      toast.success("Cadastro atualizado.");
      setMode("VIEW");
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const merge = async (confirm) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/customers/${customerId}/merge`, {
        target_id: targetId,
        confirm,
      });
      if (confirm) {
        toast.success(res.message);
        onChanged();
        onClose();
      } else {
        setPreview(res);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <ResponsiveModal open onOpenChange={onClose} title="Cliente" testid="customer-detail-modal">
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando ficha...
        </p>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title={data.name || "Cliente"}
      description={data.phone || data.phone_key}
      testid="customer-detail-modal"
    >
      <div className="space-y-4">
        {mode === "VIEW" && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Stat label="Pedidos" value={data.orders_count} data-testid="customer-orders-count" />
              <Stat label="Primeiro pedido" value={data.first_order_at ? dataBR(data.first_order_at) : "-"} />
              <Stat label="Último pedido" value={data.last_order_at ? dataBR(data.last_order_at) : "-"} />
              <Stat label="Total em pedidos" value={brl(data.orders_total)} hint="tudo que foi pedido" />
              <Stat label="Vendido" value={brl(data.sold_total)} hint={`${data.sold_count} pago(s)`} />
              <Stat
                label="Fora da venda"
                value={brl(data.cancelled_total + data.pending_total)}
                hint={`${data.cancelled_count} cancelado(s) · ${data.pending_count} pendente(s)`}
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-secondary/30 p-4" data-testid="customer-orders">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Histórico de pedidos
              </p>
              <ul className="mt-2 space-y-2">
                {data.orders.length === 0 && (
                  <li className="text-xs text-muted-foreground">Nenhum pedido vinculado ainda.</li>
                )}
                {data.orders.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">
                      <span className="font-medium">{o.number}</span>
                      <span className="text-muted-foreground"> · {dataBR(o.created_at)} · {o.status_label}</span>
                      {o.payment_status !== "CONFIRMADO" && o.status !== "CANCELADO" && (
                        <span className="text-amber-400"> · a receber</span>
                      )}
                    </span>
                    <span className="shrink-0 font-num">{brl(o.total)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(data.history || []).length > 0 && (
              <div className="rounded-xl border border-white/10 bg-secondary/30 p-4" data-testid="customer-history">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Histórico do cadastro</p>
                <ul className="mt-2 space-y-1.5">
                  {data.history.map((h, i) => (
                    <li key={i} className="flex justify-between gap-3 text-xs">
                      <span>
                        <span className="text-muted-foreground">{dataHoraBR(h.at)} · </span>
                        {h.note || h.status}
                      </span>
                      <span className="shrink-0 text-muted-foreground/70">{h.by}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() =>
                  navigate("/pedidos", {
                    state: {
                      newOrderCustomer: {
                        id: data.id,
                        name: data.name || "",
                        phone: data.phone || data.phone_key || "",
                      },
                    },
                  })
                }
                className="w-full gap-2 rounded-full sm:flex-1"
                data-testid="customer-new-order-btn"
              >
                <ShoppingCart className="h-3.5 w-3.5" /> Fazer novo pedido
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("EDIT")} className="flex-1 gap-2 rounded-full"
                      data-testid="customer-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Editar
              </Button>
              <Button variant="outline" onClick={() => setMode("MERGE")} className="flex-1 gap-2 rounded-full"
                      data-testid="customer-merge-btn">
                <Merge className="h-3.5 w-3.5" /> Unificar cadastros
              </Button>
            </div>
          </>
        )}

        {mode === "EDIT" && (
          <div className="space-y-4">
            <Field label="Nome *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="customer-name-input" />
            </Field>
            <Field label="Telefone *" hint="Com DDD. Se já pertencer a outro cliente, o sistema pede decisão.">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="customer-phone-input" />
            </Field>
            <Field label="Observações" hint="Opcional">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                     data-testid="customer-notes-input" />
            </Field>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("VIEW")} className="flex-1 rounded-full"
                      data-testid="customer-edit-cancel-btn">
                Voltar
              </Button>
              <Button onClick={save} disabled={busy} className="flex-1 rounded-full"
                      data-testid="customer-save-btn">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
              </Button>
            </div>
          </div>
        )}

        {mode === "MERGE" && (
          <div className="space-y-4">
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              Os pedidos deste cadastro passam para o cliente escolhido. Valores, itens e
              movimentações não mudam — só o vínculo.
            </p>
            <Field label="Unir com">
              <NativeSelect
                value={targetId}
                onChange={(v) => {
                  setTargetId(v);
                  setPreview(null);
                }}
                placeholder="Escolha o cadastro que vai ficar"
                options={customers
                  .filter((c) => c.id !== customerId)
                  .map((c) => ({ value: c.id, label: `${c.name} · ${c.phone || c.phone_key}` }))}
                data-testid="customer-merge-target-select"
              />
            </Field>
            {preview && (
              <div className="rounded-xl border border-white/10 bg-secondary/40 p-4 text-xs"
                   data-testid="customer-merge-preview">
                <p>{preview.message}</p>
                <p className="mt-2 text-muted-foreground">
                  {preview.source.name} ({preview.source.orders} pedido(s)) →{" "}
                  {preview.target.name} ({preview.target.orders} pedido(s)) ={" "}
                  {preview.orders_after} pedido(s)
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setMode("VIEW"); setPreview(null); }}
                      className="flex-1 rounded-full" data-testid="customer-merge-cancel-btn">
                Voltar
              </Button>
              {preview ? (
                <Button onClick={() => merge(true)} disabled={busy} className="flex-1 rounded-full"
                        data-testid="customer-merge-confirm-btn">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar unificação
                </Button>
              ) : (
                <Button onClick={() => merge(false)} disabled={busy || !targetId}
                        className="flex-1 rounded-full" data-testid="customer-merge-preview-btn">
                  Ver o que será unido
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
